import React from "react";
import { Box, Stack, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { StyledMainBox } from "../../../components/commonComponents/StyledComponents/styled";
import TextInputField from "../../../components/commonComponents/TextInputs/TextInputField";

import { Close, Save } from "@mui/icons-material";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import { useDispatch, useSelector } from "react-redux";
import { fetchWeekNumbers, DetailedSelectionIconWithTooltip, validateEmpty, validateNumbers } from "../../../api_calls/utils";
import { toast } from "react-toastify";
import { saveMarketingActivities, updateMarketingActivities } from "../../../api_calls/marketingActivities";
import { deleteItems } from "../../../Store/actions";
import { useNavigate } from "react-router-dom";
import platformConfig from "../../../platformConfig";
import SingleSelect from "../../../components/commonComponents/Selectors/SingleSelect";
import MultiSelect from "../../../components/commonComponents/Selectors/MultiSelect";
import GroupedMultiSelect from "../../../components/commonComponents/Selectors/GroupedMultiSelect";
import StoreDetailTable from "../../../components/commonComponents/DetailedStore/StoreSelectionModal";
import { loadRetailerAccessAvailability } from "../../../api_calls/retailerSelection";
import { checkUserRole } from "../../../api_calls/userManagement";
import {
  ROOT_RETAILER_GROUP_PARAM,
  resolveLoggedInUserType,
  rootRetailerOptionFields,
  shouldUseRetailerStoreHierarchy,
} from "../../../components/commonComponents/Filters/storeFilterUtils";

const AddNewMarketingActivity = ({ open, onClose, size, usedFor, closeModal, campaignRecord }) => {
  const currentStateData = useSelector((state) => state.userSelections);
  const [fieldError, setFieldError] = React.useState({});
  const [activityData, setActivityData] = React.useState({});
  const [discountRange, setDiscountRange] = React.useState({ show: false });
  const [manualActivity, setManualActivity] = React.useState({ show: false });
  const [showSelectionModal, setShowSelectionModal] = React.useState(false);
  const [retailerHierarchyAvailable, setRetailerHierarchyAvailable] = React.useState(false);
  const marketingActivityStores = Array.isArray(currentStateData.marketing_activity_stores_list)
    ? currentStateData.marketing_activity_stores_list
    : [];
  // Dropdown options from the campaign store list. Root retailer is only for grouping.
  const activityStoreOptions = marketingActivityStores.map((storeRecord) => ({
    label: storeRecord.name,
    id: storeRecord.id,
    retailer_id: storeRecord.retailer_id,
    ...rootRetailerOptionFields(storeRecord),
  }));
  const regionAndMarketForStore = (storeId) => {
    const storeRecord = marketingActivityStores.find((record) => String(record?.id) === String(storeId));
    return {
      id_region: storeRecord?.region?.id ?? null,
      market_type: storeRecord?.id_market_type ?? null,
    };
  };
  const dispatchAction = useDispatch();
  const navigate = useNavigate();
  const loggedInUserType = resolveLoggedInUserType();
  const isBrandUser =
    currentStateData?.brand_report_view === true || checkUserRole()?.user_role === "Brand";
  // Same rule as the dashboard store filter. Brand users and a single retailer stay on the flat list.
  const showRetailerHierarchy = shouldUseRetailerStoreHierarchy({
    isBrand: isBrandUser,
    retailerHierarchyAvailable,
    storeOptions: activityStoreOptions,
  });


  // console.log("test: ", typeof currentStateData.created_adlevels)
  // console.log("test: ", currentStateData.created_adlevels)

  // to calculate the discount range when discount price and original price are filled.
  const setDiscountedRange = () => {
    let discountPrice = document.getElementById("discountedPriceInput").value;
    let OriginalPrice = document.getElementById("originalPriceInput").value;

    if (OriginalPrice !== "" && discountPrice !== "") {
      // calculate the %age
      const percentageDiscount = (discountPrice / OriginalPrice) * 100;
      setDiscountRange({
        show: true,
        value: percentageDiscount.toFixed(2) + "%",
      });
    } else {
      setDiscountRange({
        show: false,
      });
    }
  };

  // to reset the pre-selected data or any new information added by user (when popup is closed)
  const reset_data = () => {
    setActivityData({});
    // reset in redux state also
    dispatchAction(
      deleteItems(["activity_ids_to_update", "activity_to_update", "activity_selections", "activity_ids_to_delete"])
    );
  };


  // validate when data is submitted
  const validateData = () => {
    let error = false;
    let errorMessages = {};
    const checkWeeks = typeof activityData.weeks !== "undefined" ? activityData.weeks : "";
    const checkStores = typeof activityData.stores !== "undefined" ? activityData.stores : "";
    const checkGtin = typeof activityData.gtin !== "undefined" ? activityData.gtin : "";

    const checkActivity = typeof activityData.activity_type !== "undefined" ? activityData.activity_type : "";
    const checkDiscount =
      typeof activityData.discounted_price !== "undefined" && activityData.discounted_price !== null
        ? parseFloat(activityData.discounted_price)
        : 0;
    const checkOriginalPrice =
      typeof activityData.original_price !== "undefined" && activityData.original_price !== null
        ? parseFloat(activityData.original_price)
        : 0;

    setFieldError({});

    //week number
    const validateWeek = validateEmpty(checkWeeks, true);
    if (validateWeek.error === true) {
      error = true;
      errorMessages = {
        ...errorMessages,
        weeks: "Please select weeks",
      };
    }
    // stores
    const validateStores = validateEmpty(checkStores, true);

    if (validateStores.error === true) {
      error = true;
      errorMessages = {
        ...errorMessages,
        stores: "Please select stores",
      };
    }
    // activity
    const validateActivity = validateEmpty(checkActivity, false);
    if (validateActivity.error === true) {
      error = true;
      errorMessages = {
        ...errorMessages,
        activity: "Please select activity",
      };
    }

    // validate GTIN
    const validateGtin = validateEmpty(checkGtin, true);

    if (validateGtin.error === true) {
      error = true;
      errorMessages = {
        ...errorMessages,
        gtin: "Please select GTIN",
      };
    }

    if (error === true) {
      setFieldError({
        error: error,
        ...errorMessages,
      });
      toast("Please fill all the required details for marketing activity", { type: "error" });
    } else {
      // update or create the marketing activities.
      if (typeof currentStateData.activity_ids_to_update !== "undefined") {
        const dataToUpdate = {
          data_to_update: activityData,
          records_to_update: currentStateData.activity_ids_to_update,
        };

        updateMarketingActivities(dataToUpdate).then((info) => {
          if (info.success === true) {
            toast("Marketing activity updated successfully.", { type: "success" });
          } else {
            toast("Sorry, something went wrong.", "error");
          }
          reset_data();
          closeModal();
          // props.closePopup(false);
        });
      } else {
        saveMarketingActivities(activityData).then((info) => {
          if (info.success === true) {
            toast("Marketing activity added successfully.", { type: "success" });
          } else {
            toast("Sorry, something went wrong.", { type: "error" });
          }
          reset_data();
          closeModal();
          // props.closePopup(false);
        });
      }
    }
  };

  // Same access check as the dashboard store filter.
  React.useEffect(() => {
    let shouldApplyResult = true;

    if (loggedInUserType === "") {
      setRetailerHierarchyAvailable(false);
      return undefined;
    }

    loadRetailerAccessAvailability(loggedInUserType)
      .then((retailerAccessInfo) => {
        if (shouldApplyResult) {
          setRetailerHierarchyAvailable(retailerAccessInfo.available === true);
        }
      })
      .catch(() => {
        if (shouldApplyResult) {
          setRetailerHierarchyAvailable(false);
        }
      });

    return () => {
      shouldApplyResult = false;
    };
  }, [loggedInUserType]);

  // Region and market type come from the campaign store record. The form has no region field.
  const applyMarketingStoreSelection = (selectedOption) => {
    let userChoices = [];
    selectedOption.forEach((chosen) => {
      if (chosen.value !== "all") {
        userChoices.push({
          store_id: chosen.id,
          ...regionAndMarketForStore(chosen.id),
        });
      } else {
        marketingActivityStores.forEach((storeRecord) => {
          userChoices.push({
            store_id: storeRecord.id,
            id_region: storeRecord?.region?.id ?? null,
            market_type: storeRecord?.id_market_type ?? null,
          });
        });
      }
    });
    setActivityData({
      ...activityData,
      stores: userChoices,
      stores_selection: selectedOption,
    });
  };

  React.useEffect(() => {
    fetchWeekNumbers(campaignRecord.campaign_start_date, campaignRecord.campaign_end_date);
    // set data for the fields (if used for update)
    if (
      typeof currentStateData.activity_to_update !== "undefined" &&
      Object.keys(currentStateData.activity_to_update).length !== 0
    ) {
      setActivityData({
        ...currentStateData.activity_to_update,
      });
    }
    // save activity data to react state (required when updating an activity)
    if (typeof currentStateData.activity_selections !== "undefined") {
      setActivityData({
        ...currentStateData.activity_selections,
      });
    } else {
      setActivityData({
        ...activityData,
        campaign_id: campaignRecord.campaign_id,
        // Seed activity with the campaign's owner company id
        company_id: campaignRecord.owner_company_id,
        year: new Date().getFullYear(),
      });
    }
  }, []);
  return (
    <>
      <Dialog
        open={open}
        onClose={(e) => {
          e.preventDefault();
        }}
        fullWidth={true}
        maxWidth={size}
      >
        <DialogTitle> Add new marketing activity</DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <StyledMainBox component="main">
            <Box>
              <Stack direction={"row"} spacing={4}>
                {/* ------- week numbers ------- */}
                <MultiSelect
                  label="Calendar Weeks*"
                  elemId={"calendar_weeks_dropdown"}
                  enableFullWidth={true}
                  disableSearch={true}
                  selectionCheck={(option, value) => option.value === value.value}
                  options={typeof currentStateData.campaign_weeks !== "undefined" ? currentStateData.campaign_weeks : []}
                  defaultVal={
                    typeof currentStateData.activity_to_update !== "undefined" &&
                      Object.keys(currentStateData.activity_to_update).length !== 0
                      ? currentStateData.activity_to_update.week_selection
                      : typeof activityData.weeks !== "undefined"
                        ? typeof currentStateData.campaign_weeks !== "undefined"
                          ? currentStateData.campaign_weeks.filter((weekInfo) => {
                            return activityData.weeks.includes(weekInfo.value);
                          })
                          : []
                        : []
                  }
                  onChangeCall={(selectedOption) => {
                    let userChoices = [];
                    selectedOption.forEach((chosen) => {
                      if (chosen.value !== "all") {
                        userChoices.push(chosen.value);
                      } else {
                        currentStateData.campaign_weeks.forEach((week) => {
                          if (week.value !== "all") {
                            userChoices.push(week.value);
                          }
                        });
                      }
                    });
                    setActivityData({
                      ...activityData,
                      weeks: userChoices,
                      week_selections: selectedOption,
                    });
                  }}
                  fieldError={
                    typeof fieldError.error !== "undefined" &&
                      fieldError.error === true &&
                      typeof fieldError.weeks !== "undefined"
                      ? fieldError.weeks
                      : ""
                  }
                />
                {/* ---------- Gtin List and Gtin Description list --------- */}
                {typeof currentStateData.gtin_list !== "undefined" && currentStateData.gtin_list.length !== 0 && (
                  <MultiSelect
                    label="GTIN*"
                    enableFullWidth={true}
                    elemId={"gtin_dropdown"}
                    selectionCheck={(option, value) => option.value === value.value}
                    options={currentStateData.gtin_list.length !== 0 ? currentStateData.gtin_list : []}
                    defaultVal={
                      typeof currentStateData.activity_to_update !== "undefined" &&
                        Object.keys(currentStateData.activity_to_update).length !== 0
                        ? currentStateData.activity_to_update.gtinSelections
                        : typeof activityData.gtin_selection !== "undefined"
                          ? activityData.gtin_selection
                          : []
                    }
                    onChangeCall={(selectedOption) => {
                      let userChoices = [];
                      selectedOption.forEach((chosen) => {
                        if (chosen.value === "all") {
                          currentStateData.gtin_list.forEach((gtinNum) => {
                            if (gtinNum.value !== "all") {
                              userChoices.push({ gtin: gtinNum.value, description: gtinNum.label.split("-")[0] });
                            }
                          });
                        } else {
                          userChoices.push({ gtin: chosen.value, description: chosen.label.split("-")[0] });
                        }
                      });
                      setActivityData({
                        ...activityData,
                        gtin: userChoices,
                        gtin_selection: selectedOption,
                      });
                    }}
                    fieldError={
                      typeof fieldError.error !== "undefined" &&
                        fieldError.error === true &&
                        typeof fieldError.gtin !== "undefined"
                        ? fieldError.gtin
                        : ""
                    }
                  />
                )}
              </Stack>
              <Box height={40} />
              <Stack direction={"row"} spacing={4}>
                {/* -------- Stores ------- */}
                {activityStoreOptions.length !== 0 && (
                  showRetailerHierarchy === true ? (
                    <GroupedMultiSelect
                      label="Stores*"
                      enableFullWidth={true}
                      elemId={"stores_dropdown"}
                      selectionCheck={(option, value) => option.label === value.label}
                      options={activityStoreOptions}
                      defaultVal={
                        typeof activityData.stores_selection !== "undefined"
                          ? activityData.stores_selection
                          : []
                      }
                      groupParam={ROOT_RETAILER_GROUP_PARAM}
                      showGroupNames={true}
                      showSelectionSummary={true}
                      onChangeCall={applyMarketingStoreSelection}
                      fieldError={
                        typeof fieldError.error !== "undefined" &&
                          fieldError.error === true &&
                          typeof fieldError.stores !== "undefined"
                          ? fieldError.stores
                          : ""
                      }
                      additionalIconWithTooltip={true}
                      additionalIconMarkup={(setOpen) => {
                        return DetailedSelectionIconWithTooltip((e) => {
                          e.stopPropagation();
                          setOpen(false);
                          setShowSelectionModal(true);
                        })
                      }}
                    />
                  ) : (
                  <MultiSelect
                    label="Stores*"
                    enableFullWidth={true}
                    elemId={"stores_dropdown"}
                    selectionCheck={(option, value) => option.label === value.label}
                    options={activityStoreOptions}
                    defaultVal={
                      typeof activityData.stores_selection !== "undefined"
                        ? activityData.stores_selection
                        : []
                    }
                    onChangeCall={applyMarketingStoreSelection}
                    fieldError={
                      typeof fieldError.error !== "undefined" &&
                        fieldError.error === true &&
                        typeof fieldError.stores !== "undefined"
                        ? fieldError.stores
                        : ""
                    }
                    additionalIconWithTooltip={true}
                    additionalIconMarkup={(setOpen) => {
                      return DetailedSelectionIconWithTooltip((e) => {
                        e.stopPropagation();
                        setOpen(false);
                        setShowSelectionModal(true);
                      })
                    }}
                    allowDuplicates={true}
                  />
                  )
                )}

                {/* --------- Activity type -------- */}
                <SingleSelect
                  enableFullWidth={true}
                  elemId={"activity_type_dropdown"}
                  label={"Activity type*"}
                  selectionCheck={(option, value) => option.value === value.value}
                  disableSearch={true}
                  options={
                    typeof currentStateData.manual_activity_list !== "undefined"
                      ? currentStateData.next_available_activity !== "not_allowed" ? [...currentStateData.manual_activity_list, ...platformConfig.activity_types] : [...currentStateData.manual_activity_list, ...platformConfig.activity_types].filter((activity) => {
                        return activity.value !== "add_manually";
                      })
                      : platformConfig.activity_types
                  }
                  defaultVal={
                    typeof currentStateData.activity_to_update !== "undefined" &&
                      Object.keys(currentStateData.activity_to_update).length !== 0
                      ? currentStateData.activity_to_update.activitySelection
                      : typeof activityData.activity_option_selected !== "undefined"
                        ? activityData.activity_option_selected
                        : ""
                  }
                  onChangeCall={(selectedOption) => {
                    if (selectedOption.value === "add_manually") {
                      setManualActivity({ show: true });
                      setActivityData({
                        ...activityData,
                        activity_name: "",
                        activity_type: currentStateData.next_available_activity,
                        activity_option_selected: selectedOption,
                      });
                    } else {
                      setManualActivity({ show: false });
                      setActivityData({
                        ...activityData,
                        activity_type: selectedOption.value,
                        activity_name: selectedOption.label,
                        activity_option_selected: selectedOption,
                      });
                    }
                  }}
                  fieldError={
                    typeof fieldError.error !== "undefined" &&
                      fieldError.error === true &&
                      typeof fieldError.activity !== "undefined"
                      ? fieldError.activity
                      : ""
                  }
                />

                {/* --------- manual activity name --------- */}
                {manualActivity.show === true && (
                  <TextInputField
                    fieldRequired={true}
                    elemId="activityNameInput"
                    label={"Activity name"}
                    fieldName="activityNameInput"
                    showlabel={true}
                    defaultVal={typeof activityData.activity_name !== "undefined" ? activityData.activity_name : ""}
                    inputType={"text"}
                    onChangeCall={(e) => {
                      setActivityData({
                        ...activityData,
                        activity_type: currentStateData.next_available_activity,
                        activity_name: e.target.value,
                      });
                    }}
                  />
                )}
              </Stack>

              <Box height={20} />
              {/* ----------- discount range ----------- */}
              {discountRange.show === true && (
                <Stack spacing={4} direction={"row"}>
                  {/* --------- discounted range --------- */}
                  <Typography variant="body2">{"Discount range"}:</Typography>
                  <Typography variant="body2">{discountRange.value}</Typography>
                </Stack>
              )}
            </Box>
          </StyledMainBox>
        </DialogContent>
        <DialogActions>
          <Stack direction={"row"} spacing={2}>
            <CloseButton
              onClick={(e) => {
                e.preventDefault();
                closeModal();
              }}
              label="Cancel"
              icon={<Close />}
            />
            <ActionButton
              onClick={(e) => {
                validateData();
              }}
              label={"Save Data"}
              icon={<Save />}
            />
          </Stack>
        </DialogActions>
      </Dialog>
      {
        showSelectionModal && (
          <StoreDetailTable
            open={showSelectionModal}
            onClose={() => {
              setShowSelectionModal(false);
              dispatchAction(deleteItems(["store_selection_pagination", "store_list_filter_applied", "detailed_stores_filtered"]));
            }}
            applyChangesOnSelect={false}
            selectedValues={typeof activityData.stores_selection !== "undefined"
              ? activityData.stores_selection.map(store => store.id)
              : []}
            toHandleApplyChanges={(selectedStoreIds, selectedStoreNames) => {
              let userChoices = [];
              selectedStoreIds.forEach((selectedId) => {
                userChoices.push({
                  store_id: selectedId,
                  ...regionAndMarketForStore(selectedId),
                });
              });
              setActivityData({
                ...activityData,
                stores: userChoices,
                stores_selection: activityStoreOptions.filter(option => selectedStoreIds.includes(option.id)),
              });
            }}
            storeOptionsList={marketingActivityStores}
          />
        )
      }
    </>
  );
};

export default AddNewMarketingActivity;
